import { openContextModal } from '@mantine/modals';
import { IncomingMessage } from 'http';
import { unique } from 'radash';
import { CollectionHelpers } from 'src/helpers/CollectionHelpers';
import { mutate } from 'swr';

import { publicConfig } from '@public-config';

import {
    ClaimInvite,
    CompanyProfile,
    CompanyProfileStubFields,
    FreeLeadsUsage,
    ManufacturerProfile,
    MembershipCatalog,
    PublishedStatus,
    StubWithLocalFiles,
    User,
} from 'models';
import { CompanyHelpers } from 'helpers/CompanyHelpers';
import { ServiceHelpers } from 'helpers/ServiceHelpers';

import { currentProfileState } from 'components/company-profile/state/current-profile';
import { InternalTrackingService } from 'services/InternalTrackingService';
import { LocalStorageService } from 'services/LocalStorageService';
import { authHeaders, PayloadSDK } from 'services/PayloadSDK';
import { UserService } from 'services/UserService';

import { ApiService } from './ApiService';

export type CompanyProfileListProps = {
    page?: number;
    depth?: number;
    select?: Record<string, boolean>;
    query?: string;
    status?: CompanyProfile['status'];
    compliance?: Partial<CompanyProfile['compliance']>;
    subscription?: CompanyProfile['subscription'];
    website?: string;
    linkedin?: string;
    sort?: string;
    ids?: string[];
    internal?: boolean;
    limit?: number;
    withLogo?: boolean;
};

// safe to use depth=1 b/c we set maxDepth on relationship fields
const CompanyProfileService = {
    navigate: ServiceHelpers.createNavigator(CompanyHelpers.urls),

    getSearchParams: (props?: CompanyProfileListProps) => {
        const {
            page,
            depth = 1,
            select = {},
            query,
            compliance = {},
            status,
            ids = [],
            sort = 'name',
            subscription,
            website,
            linkedin,
            internal,
            limit,
            withLogo,
        } = props ?? {};

        const searchParams = new URLSearchParams({
            depth: depth.toString(),
            page: page ? page.toString() : '1',
            limit: page ? '24' : '9999',
            sort,
        });

        Object.entries(select).forEach(([key, value]) => {
            searchParams.set(`select[${key}]`, value ? 'true' : 'false');
        });

        if (query) {
            searchParams.set('where[name][like]', query);
        }

        if (status) {
            searchParams.set('where[status][equals]', status);
        }

        Object.entries(compliance).forEach(([key, value], index) => {
            searchParams.set(`where[or][${index}][compliance.${key}][equals]`, (value as boolean).toString());
        });

        if (subscription) {
            searchParams.set('where[subscription][equals]', subscription);
        }

        if (website) {
            searchParams.set('where[website][like]', website);
        }

        if (linkedin) {
            searchParams.set('where[socials.linkedin][like]', linkedin);
        }

        if (ids.length) {
            searchParams.set('where[id][in]', ids.join(','));
        }

        if (internal) {
            searchParams.set('where[internal][equals]', 'true');
        }

        if (internal === false) {
            searchParams.set('where[internal][not_equals]', 'true');
        }

        if (withLogo) {
            searchParams.set('where[logos.small][exists]', 'true');
        }

        if (limit) {
            searchParams.set('limit', limit.toString());
        }

        return searchParams;
    },

    list: async (props?: CompanyProfileListProps, req?: IncomingMessage) => {
        const searchParams = CompanyProfileService.getSearchParams(props);

        return await ApiService.fetch(`${publicConfig.urls.api}/manufacturers`, 'POST', {
            body: searchParams.toString(),
            req,
            headers: {
                'X-Payload-HTTP-Method-Override': 'GET',
                'Content-Type': 'application/x-www-form-urlencoded',
            },
        });
    },

    matchExisting: async (props?: { name?: string; website?: string; linkedin?: string }) => {
        const { name, website, linkedin } = props ?? {};

        const params = new URLSearchParams({
            depth: '0',
            page: '1',
            limit: '99',
        });

        let orIndex = 0;

        if (name) {
            params.set(`where[or][${orIndex}][name][like]`, name);
            orIndex++;
        }
        if (website) {
            params.set(`where[or][${orIndex}][website][like]`, website);
            orIndex++;
        }
        if (linkedin) {
            params.set(`where[or][${orIndex}][socials.linkedin][like]`, linkedin);
            orIndex++;
        }

        return await ApiService.get(`${publicConfig.urls.api}/manufacturers?${params.toString()}`);
    },

    getRecentlyJoined: async (limit = 8, req?: IncomingMessage) =>
        await CompanyProfileService.list({ sort: '-createdAt', limit, status: PublishedStatus.PUBLISHED }, req),

    refresh: async (profileId: string, data?: CompanyProfile): Promise<CompanyProfile> => {
        const updatedProfile =
            data ?? (await ApiService.get(`${publicConfig.urls.api}/manufacturers/${profileId}?depth=0`))!;

        await mutate(CompanyHelpers.swr.profile(profileId), updatedProfile, { revalidate: false });

        if (profileId === currentProfileState.profile?.id) {
            currentProfileState.profile = updatedProfile;
        }

        return updatedProfile;
    },

    create: async (payload: any, depth: number = 1) => {
        InternalTrackingService.track('profile.create', {
            data: payload,
        });

        const result = await ApiService.post<{
            doc: CompanyProfile;
        }>(`${publicConfig.urls.api}/manufacturers?depth=${depth}`, payload, {
            throwError: true,
        });

        return result;
    },

    createFromMysExhibitor: async ({ exhibitorId, showCode }: { exhibitorId: string; showCode: string }) => {
        const result = await ApiService.post<CompanyProfile>(
            `${publicConfig.urls.api}/map-your-show/create-profile-from-exhibitor`,
            { exhibitorId, showCode },
            { throwError: true },
        );

        return result;
    },

    publish: async (id: string) => {
        InternalTrackingService.track('profile.publish', {
            profile: id,
        });

        return await ApiService.post(`${publicConfig.urls.api}/manufacturers/${id}/publish`);
    },

    get: async (manufacturerId: string, depth: number = 1, req?: IncomingMessage) => {
        const result = await ApiService.get(`${publicConfig.urls.api}/manufacturers/${manufacturerId}?depth=${depth}`, {
            req,
        });

        if (result) {
            CompanyProfileService.refresh(result.id, result);
        }

        return result;
    },

    getTeamMembers: async (manufacturerId: string) =>
        ApiService.get(`${publicConfig.urls.api}/manufacturers/${manufacturerId}/team-members`),

    getFreeLeads: async (manufacturerId: string) =>
        ApiService.get<FreeLeadsUsage>(`${publicConfig.urls.api}/manufacturers/${manufacturerId}/free-leads`),

    getByTeam: async (teamId: string, depth: number = 1, req?: IncomingMessage) => {
        return ApiService.get<{ docs: ManufacturerProfile[] }>(
            `${publicConfig.urls.api}/manufacturers?limit=99&where[team][equals]=${teamId}&depth=${depth}`,
            { req },
        );
    },

    getBySupportUser: async (userId: string) => {
        const result = await PayloadSDK.find({
            collection: 'manufacturers',
            where: {
                users: {
                    in: [userId],
                },
            },
            pagination: false,
            depth: 0,
        });

        return result as unknown as { docs: ManufacturerProfile[] };
    },

    getBySlug: async (slug: string, { depth = 1, req }: { depth?: number; req?: IncomingMessage } = {}) => {
        const { docs: companies } = await PayloadSDK.find(
            {
                collection: 'manufacturers',
                where: {
                    or: [
                        ...(CollectionHelpers.isObjectIdString(slug) ? [{ id: { equals: slug } }] : []),
                        {
                            slug: {
                                equals: slug,
                            },
                        },
                    ],
                },
                depth,
            },
            { headers: authHeaders(req) },
        );

        return companies.length === 1 ? companies[0] : null;
    },

    searchPartnerProfiles: async ({
        query,
        excludeIds,
        limit = 99,
    }: {
        query: string;
        excludeIds?: string[];
        limit?: number;
    }) => {
        const params = new URLSearchParams({
            'where[or][0][name][like]': query,
            'where[or][1][services][like]': query,
            'where[or][2][serviceTags][like]': query,
            'where[or][3][applicationTags][like]': query,
            'where[status][equals]': PublishedStatus.PUBLISHED,
            'limit': limit.toString(),
            'sort': '-completeness',
        });

        if (excludeIds?.length) {
            params.set('where[id][not_in]', excludeIds.join(','));
        }

        return ApiService.get<{
            docs: CompanyProfile[];
        }>(`${publicConfig.urls.api}/manufacturers?${params.toString()}`);
    },

    getMultiple: async (manufacturerIds: string[]) => {
        const params = manufacturerIds.map((id) => `where[id][in]=${id}`).join('&');

        if (!manufacturerIds.length) {
            return { docs: [] };
        }

        return await ApiService.get(`${publicConfig.urls.api}/manufacturers?limit=99&${params}`);
    },

    enrichCreatedBy: async (profiles: CompanyProfile[]) => {
        const userIds = unique(profiles.map((profile) => profile.createdBy).filter((id): id is string => !!id));

        const users = await UserService.getMultiple(userIds);

        return profiles.map((profile) => {
            const user = users.users.find((user) => user.id === profile.createdBy);

            return {
                ...profile,
                createdBy: user,
            } as CompanyProfile & { createdBy?: User };
        });
    },

    getServiceTags: () => {
        return ApiService.get(`${publicConfig.urls.api}/manufacturers/service-tags`);
    },

    getApplicationTags: () => {
        return ApiService.get(`${publicConfig.urls.api}/manufacturers/application-tags`);
    },

    update: async (manufacturerId: string, payload: any, depth: number = 1) => {
        InternalTrackingService.track('profile.update', {
            data: payload,
        });

        const result = await ApiService.patch(
            `${publicConfig.urls.api}/manufacturers/${manufacturerId}?depth=${depth}`,
            payload,
        );

        if (result?.doc) {
            await CompanyProfileService.refresh(result.doc.id, result.doc);
        }

        return result;
    },

    updateCatalogMembership: async (
        manufacturerId: string,
        payload: { catalog: MembershipCatalog; profileVisible?: boolean; member?: boolean },
    ) =>
        await ApiService.patch(`${publicConfig.urls.api}/manufacturers/${manufacturerId}/catalog-membership`, payload, {
            throwError: true,
        }),

    dismissUpgradeMessage: (manufacturerId: string) => {
        if (currentProfileState.profile?.id === manufacturerId) {
            currentProfileState.profile = { ...currentProfileState.profile, showUpgradeMessage: false };
        }

        return ApiService.patch(`${publicConfig.urls.api}/manufacturers/${manufacturerId}?depth=0`, {
            showUpgradeMessage: false,
        });
    },

    addSupportUser: async (manufacturerId: string, payload: any) => {
        InternalTrackingService.track('profile.update.addSupportUser', {
            profile: manufacturerId,
            data: payload,
        });

        return await ApiService.post(
            `${publicConfig.urls.api}/manufacturers/${manufacturerId}/add-support-user`,
            payload,
            {
                throwError: true,
            },
        );
    },

    requestAccess: async (manufacturerId: string, userId: string, redirect?: string) => {
        InternalTrackingService.track('profile.requestAccess', {
            profile: manufacturerId,
        });

        const response = await ApiService.post<{ success: boolean; claimed?: boolean }>(
            `${publicConfig.urls.api}/manufacturers/${manufacturerId}/request-access`,
            {
                userId,
                redirect,
            },
        );

        InternalTrackingService.track('company.accessRequest', {
            company: manufacturerId,
            claimed: response.claimed ?? false,
        });

        return response;
    },

    inviteToClaim: async (
        manufacturerId: string,
        invite: ClaimInvite,
        options: { turnstileToken: string; eventId?: string },
    ) => {
        InternalTrackingService.track('profile.inviteToClaim', {
            profile: manufacturerId,
        });

        return await ApiService.post(
            `${publicConfig.urls.api}/manufacturers/${manufacturerId}/invite-to-claim`,
            { ...invite, ...options },
            { throwError: true },
        );
    },

    moveTeam: async (
        manufacturerId: string,
        payload: { userId: string; companyId: string; previewSecret?: string },
    ) => {
        InternalTrackingService.track('profile.moveTeam', {
            profile: manufacturerId,
            data: payload,
        });

        return await ApiService.post(`${publicConfig.urls.api}/manufacturers/${manufacturerId}/move-team`, payload);
    },

    getAccessRequests: async (userId: string, status: 'pending' | 'approved' | 'rejected', companyId?: string) => {
        let url = `${publicConfig.urls.api}/companyAccessRequests?where[createdBy][equals]=${userId}&where[status][equals]=${status}`;

        if (companyId) {
            url += `&where[company][equals]=${companyId}`;
        }

        return ApiService.get(url);
    },

    denyAccess: async (manufacturerId: string, payload: { message: string; userId: string; userTeamId: string }) => {
        InternalTrackingService.track('profile.denyAccess', {
            profile: manufacturerId,
            data: payload,
        });

        return await ApiService.post(`${publicConfig.urls.api}/manufacturers/${manufacturerId}/deny-access`, payload);
    },

    cancelAccessRequest: async (manufacturerId: string, payload: { userId: string }) => {
        InternalTrackingService.track('profile.cancelAccessRequest', {
            profile: manufacturerId,
            data: payload,
        });

        return await ApiService.post(
            `${publicConfig.urls.api}/manufacturers/${manufacturerId}/cancel-access-request`,
            payload,
        );
    },

    addStub: async ({
        companyId,
        data,
        field,
    }: {
        companyId: string;
        data: StubWithLocalFiles;
        field: CompanyProfileStubFields;
    }) => {
        const result = await ApiService.post(`${publicConfig.urls.api}/manufacturers/${companyId}/stub/${field}`, data);

        if (result?.doc) {
            CompanyProfileService.refresh(result.doc.id, result.doc);
        }

        return result;
    },

    editStub: async ({
        companyId,
        data,
        stubId,
        field,
    }: {
        companyId: string;
        stubId: string;
        data: Partial<StubWithLocalFiles>;
        field: CompanyProfileStubFields;
    }) => {
        const result = await ApiService.patch(
            `${publicConfig.urls.api}/manufacturers/${companyId}/stub/${field}/${stubId}`,
            data,
        );

        if (result?.doc) {
            CompanyProfileService.refresh(result.doc.id, result.doc);
        }

        return result;
    },

    deleteStub: async ({
        companyId,
        stubId,
        field,
    }: {
        companyId: string;
        stubId: string;
        field: CompanyProfileStubFields;
    }) => {
        const result = await ApiService.delete(
            `${publicConfig.urls.api}/manufacturers/${companyId}/stub/${field}/${stubId}`,
        );

        if (result?.doc) {
            CompanyProfileService.refresh(result.doc.id, result.doc);
        }

        return result;
    },

    search: async (query: string) => {
        const params = JSON.stringify({
            search: query,
        });
        return ApiService.get<{ docs: CompanyProfile[] }>(
            `${publicConfig.urls.api}/manufacturers/search?query=${params}&sort=relevance&page=0`,
        );
    },

    getLocalSignupUrl: (teamId: string) => {
        return LocalStorageService.get(`${teamId}.localCompanySignup`);
    },

    setLocalSignup: (teamId: string, companyId?: CompanyProfile['id'], step?: number) => {
        LocalStorageService.store(`${teamId}.localCompanySignup`, CompanyHelpers.urls.create({ id: companyId, step }));
    },

    getIdFromLocalSignupUrl: (url: string) => {
        const match = url.match(/^\/profiles\/signup\/([^/?#]+)/);

        return match?.[1];
    },

    finishLocalSignup: (teamId: string) => {
        LocalStorageService.clear(`${teamId}.localCompanySignup`);
    },

    openChoosePartnerModal: (company: CompanyProfile) => {
        openContextModal({
            modal: CompanyHelpers.modals.choosePartner,
            modalId: CompanyHelpers.modals.choosePartner,
            size: 'lg',
            withCloseButton: false,
            innerProps: {
                company,
            },
        });
    },

    openCreatePartnerModal: (company: CompanyProfile) => {
        openContextModal({
            modal: CompanyHelpers.modals.createPartner,
            modalId: CompanyHelpers.modals.createPartner,
            size: 'lg',
            withCloseButton: false,
            innerProps: {
                company,
            },
        });
    },

    openRequestRemovalModal: (company: CompanyProfile) => {
        openContextModal({
            modal: CompanyHelpers.modals.requestRemoval,
            modalId: CompanyHelpers.modals.requestRemoval,
            innerProps: {
                company,
            },
        });
    },

    addPartner: async (
        company: CompanyProfile,
        partnerId: string,
        options: Partial<CompanyProfile['partners'][number]> = {},
    ) => {
        const response = await CompanyProfileService.update(company.id, {
            partners: [...(company.partners ?? []), { company: partnerId, ...options }],
        });

        return response;
    },

    regenerateEmbedding: async (companyId: string, options: { signal?: AbortSignal } = {}) => {
        return await ApiService.post(`${publicConfig.urls.api}/manufacturers/${companyId}/regenerate-embedding`, {
            signal: options.signal,
        });
    },

    mergeProfiles: async (originalId: string, contributingId: string, showChanges?: boolean) => {
        InternalTrackingService.track('profile.merge', {
            original: originalId,
            contributing: contributingId,
        });

        const result = await ApiService.post(`${publicConfig.urls.api}/manufacturers/merge`, {
            originalId,
            contributingId,
            showChanges,
        });

        return result;
    },

    deleteProfile: async (companyId: string) => {
        InternalTrackingService.track('profile.delete', {
            profile: companyId,
        });

        return await ApiService.delete(`${publicConfig.urls.api}/manufacturers/${companyId}`);
    },
};

export { CompanyProfileService };
